import { useTranslation } from 'react-i18next';
import type { FC } from 'react';
import { useState } from 'react';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { RemoveInterpreterIcon } from 'assets/svg';
import { Button } from 'features/ui/elements';
import { CollaborationStepContainer } from 'features/collaboration-base/components';
import appTheme from 'features/theme/theme';

const StyledRemoveInterpreterIcon = styled(RemoveInterpreterIcon)`
  width: 48px;
  height: 48px;
  margin-top: 16px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const RemoveTeamButton = styled(Button)`
  margin-top: ${appTheme.spacing(8)};
`;
const LeaveCallButton = styled(Button)`
  margin-top: ${appTheme.spacing(4)};
`;

const RemoveAcceptingUserButton = styled(Button)(({ theme }) => ({
  // theme is intentionally referenced for ESLint/TS compliance
  ...(theme && {}),
  display: 'flex',
  height: 44,
  padding: '10px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  alignSelf: 'stretch',
  borderRadius: 8,
  width: '100%',
  background: 'var(--button-secondary-destructive-active, #FF5247)',
}));

const GoBackButton = styled(Button)(({ theme }) => ({
  ...(theme && {}),
  display: 'flex',
  height: 44,
  padding: '10px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  alignSelf: 'stretch',
  borderRadius: 8,
  width: '100%',
  marginLeft: '0px !important',
}));
interface AcceptingUser {
  name: string;
}

interface TeamEndBaseProps {
  isShowRemoveTeamButton: boolean;
  onRemoveTeam: () => void;
  onLeaveCall: () => void;
  isLoading?: boolean;
  AcceptingAgent: AcceptingUser;
}

export const TeamEndBase: FC<TeamEndBaseProps> = ({
  isShowRemoveTeamButton,
  onRemoveTeam,
  onLeaveCall,
  isLoading = false,
  AcceptingAgent,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'teamEnd',
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleRemoveClick = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    onRemoveTeam();
  };

  return (
    <CollaborationStepContainer testId='team-end'>
      {/* TODO: update text styles when Doug finish it in Figma */}
      <Typography variant='body1' color={appTheme.palette.grey[50]}>
        {t('title')}
      </Typography>
      <Typography variant='body2' color={appTheme.palette.common.white} mt={8}>
        {t('description')}
      </Typography>
      {isShowRemoveTeamButton && (
        <RemoveTeamButton
          type='submit'
          color='primary'
          size='small'
          fullWidth
          title={t('removeTeam')}
          onClick={handleRemoveClick}
          loading={isLoading}
        >
          {t('removeTeam')}
        </RemoveTeamButton>
      )}
      <LeaveCallButton
        fullWidth
        color='secondary'
        size='small'
        title={t('leaveCall')}
        onClick={onLeaveCall}
        loading={isLoading}
      >
        {t('leaveCall')}
      </LeaveCallButton>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        slotProps={{
          paper: {
            sx: {
              background: 'var(--layer-1, #0F161A)',
              width: 320,
            },
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: 16,
          }}
        >
          <StyledRemoveInterpreterIcon aria-label='Remove Interpreter' />
          <DialogTitle
            sx={{
              flex: '1 0 0',
              alignSelf: 'stretch',
              color: 'var(--text-neutrals-extreme, #FFF)',
              textAlign: 'center',
              fontFamily: 'Inter',
              fontSize: '24px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '30px',
              letterSpacing: '-0.48px',
            }}
          >
            {t('removeAcceptingAgent', {
              acceptingAgentName: AcceptingAgent.name,
            })}
          </DialogTitle>
        </div>
        <DialogContent
          sx={{
            alignSelf: 'stretch',
            color: 'var(--text-neutrals-medium, rgba(255, 255, 255, 0.75))',
            textAlign: 'center',
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: 'inherit',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
            }}
          >
            {t('confirmRemoveAcceptingAgent', {
              acceptingAgentName: AcceptingAgent.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 7 }}>
          <RemoveAcceptingUserButton onClick={handleDialogConfirm} fullWidth>
            {t('removeButtonText')}
          </RemoveAcceptingUserButton>
          <GoBackButton onClick={handleDialogClose} fullWidth>
            {t('goBackButtonText')}
          </GoBackButton>
        </DialogActions>
      </Dialog>
    </CollaborationStepContainer>
  );
};
